// App-level state for the "move menu-items between orders" flow.
// Lives above OrderModal because the modal fully unmounts when the
// user closes it to browse the map for a target — a local ref inside
// the modal would evaporate on that transition.

import { ref, inject, provide, computed, ComputedRef, Ref } from 'vue'

const KEY_SOURCE = 'moveItemsSourceOrderId'
const KEY_SELECTED = 'moveItemsSelectedIds'
const KEY_TARGET = 'moveItemsTargetOrderId'
const KEY_ACTIONS = 'moveItemsActions'

type Actions = {
  start: (sourceOrderId: string) => void
  toggleItem: (itemId: string) => void
  setTarget: (orderId: string | null) => void
  clear: () => void
}

export const useProvideMoveItemsState = () => {
  const sourceOrderId = ref<string | null>(null)
  const selectedItemIds = ref<string[]>([])
  const targetOrderId = ref<string | null>(null)

  const start = (id: string) => {
    sourceOrderId.value = id
    selectedItemIds.value = []
    targetOrderId.value = null
  }

  const toggleItem = (itemId: string) => {
    const idx = selectedItemIds.value.indexOf(itemId)
    if (idx === -1) selectedItemIds.value.push(itemId)
    else selectedItemIds.value.splice(idx, 1)
  }

  const setTarget = (orderId: string | null) => {
    targetOrderId.value = orderId
  }

  const clear = () => {
    sourceOrderId.value = null
    selectedItemIds.value = []
    targetOrderId.value = null
  }

  const actions: Actions = { start, toggleItem, setTarget, clear }

  provide(KEY_SOURCE, sourceOrderId)
  provide(KEY_SELECTED, selectedItemIds)
  provide(KEY_TARGET, targetOrderId)
  provide(KEY_ACTIONS, actions)

  return {
    moveItemsSourceOrderId: sourceOrderId,
    moveItemsSelectedIds: selectedItemIds,
    moveItemsTargetOrderId: targetOrderId,
    ...actions,
  }
}

export const useInjectMoveItemsState = () => {
  const sourceOrderId = inject<Ref<string | null>>(KEY_SOURCE)
  const selectedItemIds = inject<Ref<string[]>>(KEY_SELECTED)
  const targetOrderId = inject<Ref<string | null>>(KEY_TARGET)
  const actions = inject<Actions>(KEY_ACTIONS)

  const noop = () => {}
  const safeActions: Actions = actions ?? {
    start: noop,
    toggleItem: noop,
    setTarget: noop,
    clear: noop,
  }

  const isActive: ComputedRef<boolean> = computed(
    () => !!sourceOrderId?.value && (selectedItemIds?.value.length ?? 0) > 0,
  )

  const isSelected = (itemId: string) =>
    selectedItemIds?.value.includes(itemId) ?? false

  return {
    moveItemsSourceOrderId: sourceOrderId ?? ref<string | null>(null),
    moveItemsSelectedIds: selectedItemIds ?? ref<string[]>([]),
    moveItemsTargetOrderId: targetOrderId ?? ref<string | null>(null),
    moveItemsIsActive: isActive,
    moveItemsIsSelected: isSelected,
    ...safeActions,
  }
}
